import React, { useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import FilterField from "./FilterField";
import { selectTriggerClassName } from "components/Form/Field/selectStyles";

export type FilterOption = {
    value: string;
    label: string;
};

type MultiSelectFilterProps = {
    disabled?: boolean;
    label: string;
    options: FilterOption[];
    selectedValues: string[];
    selectedCountLabel: (count: number) => string;
    onChange: (values: string[]) => void;
};

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = React.memo(({
    disabled = false,
    label,
    options,
    selectedCountLabel,
    selectedValues,
    onChange,
}) => {
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
    const selectedLabels = options
        .filter((option) => selectedSet.has(option.value))
        .map((option) => option.label);
    const summary = selectedLabels.length > 2 ? selectedCountLabel(selectedLabels.length) : selectedLabels.join(", ");

    return (
        <FilterField label={label}>
            <Popover>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        aria-label={label}
                        className={selectTriggerClassName}
                        disabled={disabled}
                    >
                        <span className="min-w-0 flex-1 truncate">{summary}</span>
                        <ChevronDown aria-hidden="true" className="h-4 w-4 text-gray-400" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72 rounded-lg p-2 overscroll-contain">
                    <div className="max-h-72 space-y-1 overflow-auto">
                        {options.map((option) => (
                            <label
                                key={option.value}
                                className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-gray-50"
                            >
                                <Checkbox
                                    checked={selectedSet.has(option.value)}
                                    onCheckedChange={(checked) => {
                                        const next = checked
                                            ? [...selectedValues, option.value]
                                            : selectedValues.filter((value) => value !== option.value);
                                        onChange(next);
                                    }}
                                />
                                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                            </label>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>
        </FilterField>
    );
});

MultiSelectFilter.displayName = "MultiSelectFilter";

export default MultiSelectFilter;
